import { createAsyncThunk, createSlice } from "@reduxjs/toolkit"
import { get } from "../../lib/request"

/** Normalize org user / moderator rows from nested or flat API shapes. */
export function normalizeModeratorRow(item) {
  if (!item || typeof item !== "object") return null

  const nestedUser = item.user && typeof item.user === "object" ? item.user : null
  const id =
    nestedUser?.id ??
    item.userId ??
    item.user_id ??
    item.id ??
    null
  if (id == null || String(id).trim() === "") return null

  const fullName =
    nestedUser?.fullName ??
    nestedUser?.full_name ??
    item.fullName ??
    item.full_name ??
    ""
  const username = nestedUser?.username ?? item.username ?? ""
  const email = nestedUser?.email ?? item.email ?? ""
  const accessLevel = item.accessLevel ?? item.access_level ?? ""

  return {
    id,
    fullName: String(fullName || "").trim(),
    username: String(username || "").trim(),
    email: String(email || "").trim(),
    accessLevel: String(accessLevel || "").trim(),
  }
}

function normalizeModeratorsPayload(payload) {
  const items = Array.isArray(payload)
    ? payload
    : Array.isArray(payload?.data)
      ? payload.data
      : []
  return items.map(normalizeModeratorRow).filter(Boolean)
}

// GET /api/v1/organizations/{orgId}/users
export const fetchModerators = createAsyncThunk(
  "moderators/fetch",
  async (orgId, { rejectWithValue }) => {
    try {
      if (!orgId) return rejectWithValue("orgId is required")
      const res = await get(`organizations/${orgId}/users`)
      return normalizeModeratorsPayload(res)
    } catch (err) {
      return rejectWithValue(err?.message || "Failed to fetch moderators")
    }
  }
)

const moderatorsSlice = createSlice({
  name: "moderators",
  initialState: {
    list: [],
    status: "idle",
    error: null,
  },
  reducers: {
    clearModerators: (state) => {
      state.list = []
      state.status = "idle"
      state.error = null
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchModerators.pending, (state) => {
        state.status = "loading"
        state.error = null
      })
      .addCase(fetchModerators.fulfilled, (state, action) => {
        state.status = "succeeded"
        state.list = action.payload
      })
      .addCase(fetchModerators.rejected, (state, action) => {
        state.status = "failed"
        state.error = action.payload
      })
  },
})

export const { clearModerators } = moderatorsSlice.actions
export default moderatorsSlice.reducer
